import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { useUserStore, useAuthStore } from '@/stores'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { UserPlus, Users } from 'lucide-react'
import { Identicon } from '@/components/Identicon'
import { AdminRole } from '@/types'

const UserList = observer(() => {
  const { tenantId } = useParams<{ tenantId: string }>()
  const navigate = useNavigate()
  const userStore = useUserStore()
  const authStore = useAuthStore()
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [newUser, setNewUser] = useState({
    email: '',
    name: '',
    password: ''
  })

  useEffect(() => {
    if (tenantId) {
      userStore.getUsers(tenantId)
    }
  }, [tenantId, userStore])

  const handleViewDetails = (userId: string) => {
    navigate(`/tenants/${tenantId}/users/${userId}`)
  }

  const handleCreateUser = async () => {
    if (!tenantId) return

    const result = await userStore.createUser(tenantId, {
      ...newUser,
      role: AdminRole
    })

    if (result) {
      setIsCreateDialogOpen(false)
      setNewUser({ email: '', name: '', password: '' })
      await userStore.getUsers(tenantId)
    }
  }

  const getRoleDisplay = (role: number) => {
    return role === AdminRole ? '管理者' : 'ユーザー'
  }

  return (
    <>
      <div className="space-y-6 mb-6">
        <h1 className="text-3xl font-bold">ユーザー管理</h1>
      </div>
      <Card>
        <CardHeader className="flex flex-row items-center justify-end">
          <Button 
            onClick={() => setIsCreateDialogOpen(true)}
            size="sm"
          >
            <UserPlus className="mr-2 h-4 w-4" />
            新規ユーザー
          </Button>
        </CardHeader>
        <CardContent>
          {userStore.usersArray.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>ユーザーがいません</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12"></TableHead>
                  <TableHead>名前</TableHead>
                  <TableHead>メールアドレス</TableHead>
                  <TableHead>ロール</TableHead>
                  <TableHead>登録日時</TableHead>
                  <TableHead>更新日時</TableHead>
                  <TableHead className="text-right">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {userStore.usersArray.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <Identicon source={user.email} size={32} />
                    </TableCell>
                    <TableCell className="font-medium">
                      {user.name || '-'}
                    </TableCell>
                    <TableCell>
                      {user.email}
                    </TableCell>
                    <TableCell>
                      {getRoleDisplay(user.role)}
                    </TableCell>
                    <TableCell>
                      {user.created ? new Date(user.created).toLocaleString('ja-JP') : '-'}
                    </TableCell>
                    <TableCell>
                      {user.updated ? new Date(user.updated).toLocaleString('ja-JP') : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewDetails(user.id)}
                        disabled={user.id === authStore.user?.id}
                      >
                        詳細
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>新規ユーザー作成</DialogTitle>
            <DialogDescription>
              新しいユーザーを作成します。必要な情報を入力してください。
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="email">メールアドレス</Label>
              <Input
                id="email"
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                placeholder="user@example.com"
                required
              />
            </div>
            <div>
              <Label htmlFor="name">名前</Label>
              <Input
                id="name"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                placeholder="山田 太郎"
              />
            </div>
            <div>
              <Label htmlFor="password">初期パスワード</Label>
              <Input
                id="password"
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                placeholder="8文字以上"
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsCreateDialogOpen(false)}
            >
              キャンセル
            </Button>
            <Button
              onClick={handleCreateUser}
              disabled={!newUser.email || !newUser.password || newUser.password.length < 8}
            >
              作成
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
})

export default UserList